// Utilidades de zona horaria America/Santiago (semana lunes a domingo).
// Solo usan Intl, disponible igual en Deno, Node y navegadores.

export const APP_TIME_ZONE = 'America/Santiago';

interface ZonedParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatterCache.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatterCache.set(timeZone, f);
  }
  return f;
}

export function zonedParts(instant: Date, timeZone = APP_TIME_ZONE): ZonedParts {
  const out: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(instant)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return {
    year: out.year ?? 0,
    month: out.month ?? 0,
    day: out.day ?? 0,
    hour: out.hour ?? 0,
    minute: out.minute ?? 0,
    second: out.second ?? 0,
  };
}

/** Desfase (ms) de la zona respecto de UTC en un instante dado. */
function offsetMs(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/**
 * Convierte una hora local de la zona a un instante UTC. Si la hora local no existe
 * (salto de horario de septiembre en Chile), devuelve el primer instante válido posterior.
 */
export function zonedTimeToUtc(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  timeZone = APP_TIME_ZONE,
): Date {
  const naive = Date.UTC(year, month - 1, day, hour, minute, second);
  const first = naive - offsetMs(new Date(naive), timeZone);
  const second_ = naive - offsetMs(new Date(first), timeZone);
  if (first === second_) return new Date(first);
  // Ambigüedad o hueco: usar el que, al volver a la zona, conserve la fecha/hora pedida;
  // si ninguno la conserva (hueco), el instante mayor (avanza hasta la primera hora válida).
  for (const candidate of [Math.min(first, second_), Math.max(first, second_)]) {
    const p = zonedParts(new Date(candidate), timeZone);
    if (p.year === year && p.month === month && p.day === day && p.hour === hour) {
      return new Date(candidate);
    }
  }
  return new Date(Math.max(first, second_));
}

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0');
}

/** Fecha local 'YYYY-MM-DD' de un instante en la zona. */
export function localDate(instant: Date | string, timeZone = APP_TIME_ZONE): string {
  const p = zonedParts(typeof instant === 'string' ? new Date(instant) : instant, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

/** Hora local 'HH:MM' de un instante en la zona. */
export function localTime(instant: Date | string, timeZone = APP_TIME_ZONE): string {
  const p = zonedParts(typeof instant === 'string' ? new Date(instant) : instant, timeZone);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

function parseDate(date: string): [number, number, number] {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new Error(`Fecha inválida: ${date}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** Suma días a una fecha de calendario 'YYYY-MM-DD' (sin zona). */
export function addDays(date: string, days: number): string {
  const [y, m, d] = parseDate(date);
  const t = new Date(Date.UTC(y, m - 1, d + days));
  return `${pad(t.getUTCFullYear(), 4)}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Día de la semana ISO (1 = lunes … 7 = domingo) de una fecha de calendario. */
export function isoWeekday(date: string): number {
  const [y, m, d] = parseDate(date);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return wd === 0 ? 7 : wd;
}

/** Lunes de la semana que contiene una fecha de calendario 'YYYY-MM-DD'. */
export function weekStartOfDate(date: string): string {
  return addDays(date, 1 - isoWeekday(date));
}

/** Lunes (fecha local) de la semana que contiene el instante, en America/Santiago. */
export function weekStart(instant: Date | string, timeZone = APP_TIME_ZONE): string {
  return weekStartOfDate(localDate(instant, timeZone));
}

/** Medianoche local (o primera hora válida) de una fecha de calendario, como instante UTC. */
export function startOfLocalDay(date: string, timeZone = APP_TIME_ZONE): Date {
  const [y, m, d] = parseDate(date);
  return zonedTimeToUtc(y, m, d, 0, 0, 0, timeZone);
}

/**
 * Convierte el valor `start`/`end` de una propiedad date de Notion a instante ISO UTC.
 * Las fechas sin hora ('YYYY-MM-DD') se interpretan como medianoche en America/Santiago.
 */
export function notionDateToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return startOfLocalDay(value).toISOString();
  const t = Date.parse(value);
  return Number.isNaN(t) ? null : new Date(t).toISOString();
}
